"use client";

import {
  ConfirmEmailResponseType,
  ConfirmEmailType,
  ImpersonateResponseType,
  ImpersonateUserType,
  RecoverPasswordResponseType,
  RecoverPasswordType,
  SelectInstanceType,
  SignInResponseType,
  SignInType,
  SignUpType,
  UnimpersonateResponseType
} from "@/domains/auth/auth.type";
import { getApiClient, createRawApiClient } from "@/shared/api/axios";
import { handleApiError } from "@/shared/api/error-handler";
import { getRefreshToken } from "./auth-storage";

/**
 * Realiza o login do usuário
 * @param data Dados de login
 * @returns Resposta da API com os dados do usuário e token
 */
export async function signInApi(data: SignInType): Promise<SignInResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.post<SignInResponseType>("/auth/signin", data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Realiza o cadastro do usuário
 * @param data Dados de cadastro
 * @returns Resposta da API com os dados do usuário e token
 */
export async function signUpApi(data: SignUpType): Promise<SignInResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.post<SignInResponseType>("/auth/signup", data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Envia o link de recuperação de senha para o e-mail informado
 * @param data E-mail do usuário
 * @returns Resposta da API
 */
export async function recoverPasswordApi(data: RecoverPasswordType): Promise<RecoverPasswordResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.post<RecoverPasswordResponseType>("/auth/recover-password", data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Realiza o logout do usuário
 * @returns Resposta da API
 */
export async function signOutApi(): Promise<{ status: string; message: string[] }> {
  try {
    const api = await getApiClient();
    const response = await api.post("/auth/signout");
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Renova o token de acesso
 * @returns Resposta da API com novo token
 */
export async function refreshTokenApi(): Promise<SignInResponseType> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    // Lançar um erro específico que o interceptor possa identificar se necessário
    const error = new Error("Refresh token não encontrado.");
    (error as any).statusCode = 401; // Simular um erro 401 para consistência no tratamento
    throw error;
  }

  try {
    const api = createRawApiClient();
    const response = await api.post<SignInResponseType>(
      "/auth/refresh-token",
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Valida o token de acesso
 * @returns Resposta da API
 */
export async function validateTokenApi(): Promise<{ status: string; message: string[] }> {
  try {
    const api = await getApiClient();
    const response = await api.post("/auth/validate");
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Seleciona uma instância para o usuário
 * @param data Dados da instância
 * @returns Resposta da API com os dados do usuário e token
 */
export async function selectInstanceApi(data: SelectInstanceType): Promise<SignInResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.post<SignInResponseType>("/auth/select-instance", data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Personifica um usuário
 * @param data Dados de personificação (token e instance)
 * @returns Tokens de autenticação com informações do usuário personificado
 */
export async function impersonateUserApi(data: ImpersonateUserType): Promise<ImpersonateResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.post<ImpersonateResponseType>("/auth/impersonate", data);

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Despersonifica o usuário atual
 * @returns Tokens de autenticação do usuário original
 */
export async function unimpersonateUserApi(): Promise<UnimpersonateResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.post<UnimpersonateResponseType>("/auth/unimpersonate");

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Confirma o email do usuário
 * @param data Token de confirmação
 * @returns Resposta da API
 */
export async function confirmEmailApi(data: ConfirmEmailType): Promise<ConfirmEmailResponseType> {
  try {
    const api = await getApiClient();
    const response = await api.post<ConfirmEmailResponseType>("/auth/confirm-email", data);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
